import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import type { EventFlag, FlagTone, QuoteStatusKey } from "@/lib/event-board";

/** Pill colours from the prototype (p-ok, p-warn, p-hot, p-mute). */
const FLAG_TONE_CLASS: Record<FlagTone, string> = {
  ok: "bg-[hsl(var(--ev-ok-soft))] text-[hsl(var(--ev-ok))]",
  warn: "bg-[hsl(var(--ev-warn-soft))] text-[hsl(var(--ev-warn))]",
  hot: "bg-[hsl(var(--ev-hot-soft))] text-[hsl(var(--ev-hot))]",
  mute: "border bg-background text-muted-foreground",
};

function FlagPill({ tone, children, className }: { tone: FlagTone; children: ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        "inline-block rounded-full px-[9px] py-0.5 text-[11.5px] font-bold tracking-[.02em]",
        FLAG_TONE_CLASS[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

/**
 * A card's or List row's flags. On a card the pills wrap their text; in the
 * List they stay on one line.
 */
export function EventFlags({
  flags,
  extra = [],
  wrapText = false,
}: {
  flags: EventFlag[];
  /** Post-event "Paid", "Thanked", "Review asked" (List only). */
  extra?: string[];
  wrapText?: boolean;
}) {
  if (flags.length === 0 && extra.length === 0) return null;
  const pillClass = wrapText ? "whitespace-normal tracking-normal" : "whitespace-nowrap";
  return (
    <div className="flex flex-wrap gap-[5px]">
      {flags.map((f) => (
        <FlagPill key={f.text} tone={f.tone} className={pillClass}>
          {f.text}
        </FlagPill>
      ))}
      {extra.map((t) => (
        <FlagPill key={t} tone="ok" className={pillClass}>
          {t}
        </FlagPill>
      ))}
    </div>
  );
}

const DOT_CLASS: Record<QuoteStatusKey, string> = {
  none: "bg-border",
  draft: "bg-muted-foreground",
  sent: "bg-[hsl(var(--ev-info))]",
  accepted: "bg-[hsl(var(--ev-ok))]",
  declined: "bg-[hsl(var(--ev-hot))]",
};

/** The quote status dot (the prototype's `.qs i`). */
export function QuoteStatusDot({ status }: { status: QuoteStatusKey }) {
  return <span aria-hidden className={cn("h-[7px] w-[7px] shrink-0 rounded-full", DOT_CLASS[status])} />;
}
